/**
 * Wire-format breakdown granularity for AI-generated charts.
 * Role, skill and industry shares classify children versus peers; resolved
 * filter kinds supply their actual API tiers. Children retain the parent's
 * filters and use the frontend's drill-down default.
 *
 * WHY THIS EXISTS: `geoBreakdownLevelFromFilters` (the FE composition-v3
 * default) is a DRILL-DOWN rule — it breaks down one level below the applied
 * geo filter and defaults to region with none. The share pipeline correctly
 * PRUNES the asked segment's filter ("the US" — filtering to it would make
 * its share 100%), which also erased the only granularity signal, so "what
 * share is in the US" charted REGIONS and could not name a US series (live
 * 2026-08-26). The pruner knows exactly what it removed; this module turns
 * that knowledge into an explicit, plot-carried granularity.
 *
 * SEMANTICS: the level is the pruned segment's OWN kind, not drill-down —
 * the asked segment must exist as a series on the axis to be readable.
 *
 * Kept as strings (not the data-access `EntKind` enum) so dash-meta and
 * libs/core stay decoupled from the FE's generated gql types; the FE maps
 * these to `EntKind` in `plotBreakdownLevelsToOverrides`.
 */

export const GEO_BREAKDOWN_LEVELS = [
  'region',
  'country',
  'state',
  'metro',
  'county',
  'city',
] as const;

export type GeoBreakdownLevel = (typeof GEO_BREAKDOWN_LEVELS)[number];

/** Explicit API tiers carried to the chart; absent tiers keep the FE drill-down default. */
export type PlotBreakdownLevels = {
  geography?: GeoBreakdownLevel;
  role?: number;
  skills?: number;
  rics?: number;
};

/**
 * Resolved-filter expr kinds → wire level. This covers every kind listed in
 * `BREAKDOWN_DIMENSION_TO_EXPR_KINDS.geography` (see ../share-display): the
 * five `pos_attribute` kinds and their five `USER_*` twins, exactly.
 *
 * County has no entry on purpose: it is a level a chart can be drawn at, but
 * there is no COUNTY *expr kind* in the geography set above, so the pruner can
 * never report one — an unmapped kind simply keeps today's default.
 */
const EXPR_KIND_TO_GEO_LEVEL: Record<string, GeoBreakdownLevel> = {
  REGION: 'region',
  USER_REGION: 'region',
  COUNTRY: 'country',
  USER_COUNTRY: 'country',
  STATE: 'state',
  USER_STATE: 'state',
  MSA: 'metro',
  USER_MSA: 'metro',
  CITY: 'city',
  USER_CITY: 'city',
};

/** The geography level a resolved-filter expr kind sits at, if it is one. */
export const geoLevelOfExprKind = (
  kind: string
): GeoBreakdownLevel | undefined => EXPR_KIND_TO_GEO_LEVEL[kind];

/**
 * The granularity implied by the geo filter kinds the share pruner removed.
 * All pruned geo kinds must agree on ONE level; mixed granularities ("US vs
 * NYC") return undefined — no single level renders both as series, so the
 * conservative move is today's default plus honest labels.
 */
export const geoBreakdownLevelFromPrunedKinds = (
  prunedKinds: readonly string[]
): GeoBreakdownLevel | undefined => {
  const levels = new Set<GeoBreakdownLevel>();
  for (const kind of prunedKinds) {
    const level = EXPR_KIND_TO_GEO_LEVEL[kind];
    if (level !== undefined) levels.add(level);
  }
  if (levels.size !== 1) return undefined;
  return [...levels][0];
};

/** Relative levels avoid guessing taxonomy-specific depths from occupation or skill names. */
const HIERARCHICAL_BREAKDOWN_LEVELS = ['children', 'peers'] as const;
export const SHARE_BREAKDOWN_LEVELS = [
  ...GEO_BREAKDOWN_LEVELS,
  ...HIERARCHICAL_BREAKDOWN_LEVELS,
] as const;
export type ShareBreakdownLevel = (typeof SHARE_BREAKDOWN_LEVELS)[number];

export const isGeoBreakdownLevel = (
  level: string | undefined
): level is GeoBreakdownLevel =>
  level !== undefined &&
  (GEO_BREAKDOWN_LEVELS as readonly string[]).includes(level);

export const isHierarchyDimension = (dimension: string | undefined): boolean =>
  dimension === 'role' || dimension === 'skills' || dimension === 'rics';

export const isBreakdownLevelForDimension = (
  dimension: string | undefined,
  level: ShareBreakdownLevel | undefined
): boolean =>
  dimension === 'geography'
    ? isGeoBreakdownLevel(level)
    : isHierarchyDimension(dimension) &&
      (level === 'children' || level === 'peers');

/** Expr kinds use picker positions; API tiers follow the reference taxonomy. */
const TAXONOMY_LEVELS_BY_KIND = {
  role: {
    ROLE_K7: 1,
    ROLE_K150: 3,
    ROLE_K1500: 5,
    ROLE_K15000: 7,
    ROLE_K17000: 7,
  },
  skills: {
    SKILL_LVL_1: 3,
    SKILL_LVL_2: 5,
    SKILL_LVL_3: 6,
    SKILL_LVL_4: 7,
    SKILL_LVL_5: 8,
  },
  rics: { RICS_K10: 10, RICS_K50: 50, RICS_K400: 400 },
} as const;

export type TaxonomyBreakdownLevel<
  D extends keyof typeof TAXONOMY_LEVELS_BY_KIND,
> =
  (typeof TAXONOMY_LEVELS_BY_KIND)[D][keyof (typeof TAXONOMY_LEVELS_BY_KIND)[D]];
/** Accepted wire tiers derive from the same mapping used by share pruning. */
export const TAXONOMY_BREAKDOWN_LEVELS: Record<
  keyof typeof TAXONOMY_LEVELS_BY_KIND,
  readonly number[]
> = {
  role: [...new Set(Object.values(TAXONOMY_LEVELS_BY_KIND.role))],
  skills: [...new Set(Object.values(TAXONOMY_LEVELS_BY_KIND.skills))],
  rics: [...new Set(Object.values(TAXONOMY_LEVELS_BY_KIND.rics))],
};

export const taxonomyLevelOfExprKind = (
  dimension: string,
  kind: string
): number | undefined =>
  (TAXONOMY_LEVELS_BY_KIND as Record<string, Record<string, number>>)[
    dimension
  ]?.[kind];

export const taxonomyBreakdownLevelsFromPrunedKinds = (
  dimension: string,
  kinds: readonly string[]
): PlotBreakdownLevels => {
  if (!isHierarchyDimension(dimension)) return {};
  const levels = new Set(
    kinds
      .map((kind) => taxonomyLevelOfExprKind(dimension, kind))
      .filter((level): level is number => level !== undefined)
  );
  return levels.size === 1 ? { [dimension]: [...levels][0] } : {};
};
